import { useMemo } from 'react';

import {
    isUpcomingOpenShift,
    useCoverageOpenShiftWindow,
} from '@apps/web/features/coverage/model/open_shifts';
import { useCalendarShiftsInRange } from '@apps/web/swr_data_fetching/data_fetching_hooks';

/** Reuse the calendar's complete, bounded range; never fetch once per client. */
export function useCoverageOpenShiftClients(
    organizationId: string | null,
    enabled: boolean,
) {
    const { rangeStart, rangeEnd, refresh, isReady } =
        useCoverageOpenShiftWindow(enabled);
    const result = useCalendarShiftsInRange(
        organizationId,
        { reason: 'coverage-open-shift-clients' },
        { shiftStartDate: rangeStart, shiftEndDate: rangeEnd },
        { enabled: enabled && isReady, revalidateOnFocus: false },
    );
    const isLoading =
        (enabled && !isReady) || result.isLoading || result.hasPendingPages;
    const clients = useMemo(() => {
        // Partial pages must not look like a complete client census.
        if (!enabled || isLoading || result.error) {
            return [];
        }
        const byId = new Map<
            string,
            { readonly id: string; readonly name: string }
        >();
        for (const shift of result.shifts) {
            if (
                shift.client_id &&
                isUpcomingOpenShift(shift, new Date(rangeStart).getTime())
            ) {
                byId.set(shift.client_id, {
                    id: shift.client_id,
                    name: shift.patient_name,
                });
            }
        }
        return [...byId.values()].sort(
            (a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id),
        );
    }, [enabled, isLoading, result.error, result.shifts, rangeStart]);
    return {
        clients,
        isLoading,
        error: result.error,
        retry: refresh,
    };
}
